import { useTransactionKccReadiness } from './use_transaction_kcc_readiness';
import { isKfhtradeSecurityTransaction } from '../../../../shared/transactionApprovals';
import { Alert, Button, Grid, Loader, Stack, Group, Box, Paper, Text, } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import React, { useContext, useEffect, useState } from 'react'
import { MyUserDataContext } from '../../contexts';
import { httpsCallable } from 'firebase/functions';
import { Action, ActionType, BaseResult, TransactionStatusCode, Approval, RejectionFormValues, CustomClaims, SendTransactionSMSRequest, FirebaseTransaction } from '../../types';

import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import CustomRejectionModal from '../../components/custom_rejection_modal';
import CustomProductTimeline from '../../components/custom_product_timeline';
import { collection, doc, DocumentReference, onSnapshot } from 'firebase/firestore';
import { MessagesSent } from '../../components/send_sms_modal';



const TransactionsActions = ({ transactionObj, transactionRef }: { transactionObj?: FirebaseTransaction; transactionRef?: DocumentReference }) => {
  const [pending, setPending] = useState<any>(false)
  const appContext = useContext(MyUserDataContext);
  const functions = useFunctions();
  const [msg, setMsg] = React.useState(null as any);
  const [isError, setIsError] = React.useState(false);
  const [SMSDocID, setSMSDocID] = useState<string>();
  const [draftActionLoading, setDraftActionLoading] = useState(false);
  const userData = useContext(MyUserDataContext);
  let userClaims = userData.firebaseTokenResult?.claims.customClaims as CustomClaims || {};

  const db = useFirestore();

  const [transactionPrivateData, setTransactionPrivateData] = useState<Approval[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState<string>();
  const customerID = transactionObj?.customerID ?? transactionObj?.customerObj?.id;
  const transactionPath = transactionRef?.path ?? (customerID && transactionObj?.id
    ? `customers/${customerID}/transactions/${transactionObj.id}` : undefined);

  useEffect(() => {
    setTransactionPrivateData([]);
    setActionsError(undefined);
    setActionsLoading(!!transactionPath);
    if (!transactionPath) return;

    return onSnapshot(doc(db, `${transactionPath}/private/actions`), snapshot => {
      const approvals = snapshot.data()?.approvals;
      setTransactionPrivateData(Array.isArray(approvals) ? approvals : []);
      setActionsLoading(false);
    }, error => {
      console.error('Unable to load transaction actions', { code: error.code });
      setActionsError('Unable to load transaction actions. Please refresh or contact support.');
      setTransactionPrivateData([]);
      setActionsLoading(false);
    });
  }, [db, transactionPath]);

  let actionToTakeLabel: string | null = null;
  let actionToTake: ActionType | null = null;
  let actionColor: string = "";
  let transactionStatusCode = transactionObj?.tags?.transactionStatus?.code as TransactionStatusCode;

  if (transactionStatusCode === TransactionStatusCode.waiting_for_execution && transactionObj?.source === 'kfhtrade') {
    actionToTakeLabel = "Waiting for Operations Approval";
    actionToTake = "execution";
    actionColor = "blue";
  } else if (transactionStatusCode === TransactionStatusCode.authorized_signatory_approval && transactionObj && isKfhtradeSecurityTransaction(transactionObj)) {
    actionToTakeLabel = 'Executive Approval (Authorized Signatory)';
    actionToTake = 'authorized_signatory_approve';
    actionColor = "orange";
  } else if (transactionStatusCode === TransactionStatusCode.approval) {
    actionToTakeLabel = 'CR Approval';
    actionToTake = 'approval';
    actionColor = "green";
  }

  const kccReadiness = useTransactionKccReadiness(transactionObj, actionToTake === 'execution' && !!transactionObj && isKfhtradeSecurityTransaction(transactionObj));

  const handleStatusChange = async (status: ActionType) => {
    if (status === 'execution' && !kccReadiness.ready) return;
    setPending(true)
    setMsg(null)
    setIsError(false)

    let token = await appContext.getAzureADToken!(appContext);

    if (!token) {
      console.log("No AD token");
      setMsg("No Azure token.  Please login again.");
      setIsError(true);
    }

    const actionValues: Action = {
      transactionID: transactionObj?.id,
      customerID: transactionObj?.customerID,
      action: status,
      token: token!
    }
    const takeAction = httpsCallable<Action, BaseResult>(functions, 'transactionAction');
    try {
      console.log("Taking action in handleStatusChange", actionValues);
      const result = await takeAction(actionValues);
      setPending(false);
      console.log("result is ", result);
      return result
    } catch (error: any) {
      console.log("error is ", error);
      setMsg(error.message);
      setIsError(true);
    }
    setPending(false)
  }


  // Custom rejection handler for transactions
  const handleTransactionRejection = async (values: RejectionFormValues): Promise<void> => {
    try {
      const token = await appContext.getAzureADToken!(appContext);
      if (!token) {
        throw new Error("No Azure token. Please login again.");
      }

      console.log("Transaction rejection values:", values);

      const actionValues: Action = {
        transactionID: transactionObj?.id,
        customerID: transactionObj?.customerID,
        action: "reject",
        token: token,
        comments: values.comments,
        sendSMS: values.sendSMS,
        linkInfo: {
          smsMessage: values.linkInfo?.smsMessage,
          updateType: values.linkInfo?.updateType,
          senderID: values.linkInfo?.senderID,
          usePACI: values.usePACI,
        },
        missingFiles: values.missingFiles || [],
      };

      const takeAction = httpsCallable<Action, BaseResult>(functions, 'transactionAction');
      const result = await takeAction(actionValues);
      console.log("Transaction rejection result:", result);

      setMsg("Transaction rejected successfully");
      setIsError(false);
    } catch (error: any) {
      console.error("Transaction rejection error:", error);
      setMsg(error.message || "Failed to reject transaction");
      setIsError(true);

      // Re-throw the error so the modal can handle it (show error in modal)
      throw error;
    }
  };

  console.log("transactionObj============>>>>>>>>", transactionObj);

  if (!Object.keys(transactionObj || {}).length)
    return <></>

  const handleTransactionContractMessage = async (smsDocID?: string) => {
    setDraftActionLoading(true);
    setMsg(null);
    setIsError(false);

    try {
      const sendTransactionContractMessage = httpsCallable<SendTransactionSMSRequest, BaseResult>(functions, "sendTransactionContractMessage");
      const token = await userData.getAzureADToken!(userData);
      if (!token) {
        throw new Error("No Microsoft token.  Try refreshing");
      }
      //generate a doc ID
      let docID = smsDocID;
      if (!smsDocID) {

        docID = doc(collection(db, "sms")).id;
        setSMSDocID(docID);
      }
      if (!docID) {
        throw new Error("Failed to generate message tracking ID. Please try again.");
      }

      // The server reads the current transaction and customer.
      await sendTransactionContractMessage({
        transaction: {
          securities: transactionObj!.securities || [],
          transactionType: transactionObj!.transactionType,
          id: transactionObj!.id,
          customerID,
        },
        token: token!,
        smsDocID: docID,
        senderID: "KFHCapital",
        type: "whatsapp"
      });

      setMsg('Transaction contract message sent via WhatsApp.');
    } catch (error: any) {
      console.error('Failed to send transaction contract message:', error);
      setIsError(true);
      setMsg(error.message || 'Failed to send transaction contract message. Please try again.');
    } finally {
      setDraftActionLoading(false);
    }

  }
  return (
    <Paper withBorder radius="md" p="md" w="100%">
      <Stack gap="md">
      <Text fw={600}>Transaction Actions</Text>
      {/* Show transaction Actions details */}
      {actionsLoading && <Loader size="sm" aria-label="Loading transaction actions" />}
      {actionsError && <Alert color="red" title="Transaction actions unavailable">{actionsError}</Alert>}
      {!actionsLoading && !actionsError && transactionPrivateData.length === 0 && (
        <Text size="sm" c="dimmed">No transaction actions recorded yet.</Text>
      )}
      {transactionPrivateData.length > 0 && (
        <>
          <Box w="100%">
            <CustomProductTimeline approval={transactionPrivateData} />
          </Box>
        </>
      )}

      <Box w="100%">

        {msg && <Alert
          style={{ marginTop: 20 }}
          icon={<IconAlertCircle size={16} />}
          c={isError ? "red" : "green"}
          title={isError ? "Failure" : "Success"}
        >
          {msg}
        </Alert>
        }
      </Box>

      {transactionStatusCode === "need_signature" &&

        <Box w="100%">
          <Grid>
            <Grid.Col span={6}>
              <Button type="button" name="send_contract" loading={draftActionLoading} onClick={async () => {
                await handleTransactionContractMessage();
              }}>
                Re-send Transaction Contract
              </Button>
            </Grid.Col>


            <Grid.Col span={6}>
              {(SMSDocID) &&

                <MessagesSent docID={SMSDocID} handleFailedMessages={() => {
                  handleTransactionContractMessage(SMSDocID);
                }} />

              }
            </Grid.Col>
          </Grid>


        </Box>
      }
      {
        (actionToTake !== null) &&

        <>
          {!pending ?
            <>
              <Group mt={12}>
                <Box w="100%">
                  <Stack gap="sm">
                    {actionToTake === 'execution' && kccReadiness.loading && <Text size="sm" c="dimmed">Checking securities KCC numbers…</Text>}
                    {actionToTake === 'execution' && kccReadiness.missing.length > 0 && <Alert color="orange" title="KCC numbers required">
                      Add a KCC number for every security before Operations approval. Missing or unresolved: {kccReadiness.missing.join(', ')}.
                    </Alert>}
                    {actionToTake && (
                      <Button
                        disabled={actionToTake === 'execution' && !kccReadiness.ready}
                        color={actionColor ?? "green"}
                        fullWidth
                        onClick={() => handleStatusChange(actionToTake as ActionType)}
                      >
                        {actionToTakeLabel ?? "Approve"}
                      </Button>
                    )}
                    <CustomRejectionModal
                      onSubmit={handleTransactionRejection}
                      title="Reject Transaction"
                      submitButtonText="Reject Transaction"
                      submitButtonColor="red"
                      triggerButtonText="Reject"
                      triggerButtonColor="red"
                      triggerButtonDisabled={false}
                      triggerButtonSize="md"
                      triggerButtonVariant="filled"
                      showSMSOptions={true}
                      showUpdateLinkOptions={true}
                      initialValues={{
                        linkInfo: {
                          senderID: "",
                          updateType: "none"
                        }
                      }}
                    />
                  </Stack>
                </Box>
              </Group>
            </> :
            <>
              <Box w="100%">

                <Alert
                  style={{ marginTop: 20 }}
                  icon={<Loader />}

                >
                  Sending Request

                </Alert>
              </Box>
            </>}

        </>
      }
      </Stack>
    </Paper>
  )
}

export default TransactionsActions
